import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown, ChevronUp, FileCheck, X } from 'lucide-react';
import { getDecisions, getAuditTrace, type DecisionObjectStored, type AuditRecord, type DecisionsResponse, type AuditTraceResponse } from '../utils';

interface RiskIntelligenceSectionProps {
  symbol: string;
}

export const RiskIntelligenceSection: React.FC<RiskIntelligenceSectionProps> = ({ symbol }) => {
  const [decisions, setDecisions] = useState<DecisionObjectStored[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [auditDecisionId, setAuditDecisionId] = useState<string | null>(null);
  const [auditTrace, setAuditTrace] = useState<AuditRecord[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    if (!symbol || !expanded) {
      setDecisions([]);
      return;
    }
    setLoading(true);
    setError(null);
    getDecisions(symbol)
      .then((res: DecisionsResponse) => {
        if (res.success) {
          // success with a null/empty list is the honest empty state ("no decisions yet"),
          // NOT an error — only a real failure (success=false) should surface an error.
          setDecisions(res.decision_objects || []);
        } else {
          setError(res.error || 'Failed to load decisions');
        }
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Network error');
      })
      .finally(() => setLoading(false));
  }, [symbol, expanded]);

  const openAudit = (decisionId: string) => {
    setAuditDecisionId(decisionId);
    setAuditTrace([]);
    setAuditLoading(true);
    getAuditTrace(decisionId)
      .then((res: AuditTraceResponse) => {
        if (res.success && res.trace) {
          setAuditTrace(res.trace);
        }
      })
      .finally(() => setAuditLoading(false));
  };

  const formatDate = (s?: string) => {
    if (!s) return '—';
    try {
      const d = new Date(s);
      return d.toLocaleString();
    } catch {
      return s;
    }
  };

  return (
    <section id="risk-intelligence-section" className="py-6 md:py-8 px-4 md:px-10 lg:px-20">
      <div className="max-w-[1600px] mx-auto">
        <motion.button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between gap-3 bg-[color:var(--c-panel-raised)] rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.06)] hover:border-[color:rgb(var(--c-ink-rgb)/0.12)] transition-all duration-300 py-4 px-4 md:px-6 text-left"
          whileHover={{ scale: 1.002 }}
          whileTap={{ scale: 0.998 }}
        >
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 shrink-0 text-[color:var(--c-gray-500)]" />
            <div>
              <h3 className="text-lg font-bold text-[color:var(--c-gray-100)]">Risk & Intelligence OS</h3>
              <p className="text-sm text-[color:var(--c-gray-400)]">Decision Objects & Audit trail for {symbol.toUpperCase()}</p>
            </div>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 shrink-0 text-[color:var(--c-gray-400)]" /> : <ChevronDown className="w-5 h-5 shrink-0 text-[color:var(--c-gray-400)]" />}
        </motion.button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="mt-4 rounded-md border border-[color:rgb(var(--c-ink-rgb)/0.06)] bg-[color:rgb(var(--c-panel-black-rgb)/0.8)] p-6">
                {loading && (
                  <p className="text-[color:var(--c-gray-400)] flex items-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-[color:rgb(var(--c-ink-rgb)/0.15)] border-t-[color:var(--c-gray-400)] rounded-full animate-spin" />
                    Loading decisions...
                  </p>
                )}
                {error && <p className="text-red-400">{error}</p>}
                {!loading && !error && decisions.length === 0 && (
                  <p className="text-[color:var(--c-gray-400)]">No decisions yet. Send analysis to ARIN (v2) to create a Decision Object.</p>
                )}
                {!loading && !error && decisions.length > 0 && (
                  <ul className="space-y-3">
                    {decisions.map((d, i) => {
                      const defi = d.analysis_snapshot?.defi_exposure;
                      const tvlChange = defi?.tvl_change_7d;
                      const protocolRisks = defi?.protocol_risks;
                      return (
                        <li
                          key={d.decision_id || d.object_id + i}
                          className="flex flex-wrap items-center justify-between gap-4 py-3 border-b border-[color:rgb(var(--c-ink-rgb)/0.06)] last:border-0"
                        >
                          <div className="flex flex-wrap items-center gap-3 flex-1">
                            <FileCheck className="w-5 h-5 text-[color:var(--c-gray-500)] shrink-0" />
                            <div className="min-w-0">
                              <span className="font-mono text-sm text-[color:var(--c-gray-100)]">{d.decision_id || d.object_id}</span>
                              <span className="text-[color:var(--c-gray-400)] text-sm ml-2">{formatDate(d.created_at || d.timestamp)}</span>
                              {(tvlChange != null || (protocolRisks && protocolRisks.length > 0)) && (
                                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                  {tvlChange != null && (
                                    <span className="text-[color:var(--c-gray-400)]">
                                      DeFi TVL 7d: <span className={tvlChange >= 0 ? 'text-green-400' : 'text-red-400'}>{tvlChange >= 0 ? '+' : ''}{tvlChange.toFixed(1)}%</span>
                                    </span>
                                  )}
                                  {protocolRisks && protocolRisks.length > 0 && (
                                    <span className="text-[color:var(--c-gray-400)]">Risks: {protocolRisks.join(', ')}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => openAudit(d.decision_id || (d as any).DecisionID || '')}
                            className="text-sm font-semibold text-[color:var(--c-gray-400)] hover:underline shrink-0"
                          >
                            View audit trace
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Audit trace modal */}
      {auditDecisionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setAuditDecisionId(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[color:var(--c-panel-raised)] border border-[color:rgb(var(--c-ink-rgb)/0.06)] rounded-md shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[color:rgb(var(--c-ink-rgb)/0.06)]">
              <h4 className="text-lg font-bold text-[color:var(--c-gray-100)]">Audit trace: <span className="font-mono text-sm">{auditDecisionId}</span></h4>
              <button onClick={() => setAuditDecisionId(null)} className="p-1 text-[color:var(--c-gray-400)] hover:text-[color:var(--c-ink)]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {auditLoading && (
                <p className="text-[color:var(--c-gray-400)] flex items-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-[color:rgb(var(--c-ink-rgb)/0.15)] border-t-[color:var(--c-gray-400)] rounded-full animate-spin" />
                  Loading trace...
                </p>
              )}
              {!auditLoading && auditTrace.length === 0 && <p className="text-[color:var(--c-gray-400)]">No audit records.</p>}
              {!auditLoading && auditTrace.length > 0 && (
                <ul className="space-y-3">
                  {auditTrace.map((r) => (
                    <li key={r.id} className="rounded-md bg-[color:var(--c-panel-black)] p-3 border border-[color:rgb(var(--c-ink-rgb)/0.06)]">
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold text-[color:var(--c-gray-400)]">{r.category}</span>
                        <span className="text-[color:var(--c-gray-400)]">{formatDate(r.timestamp)}</span>
                      </div>
                      {r.symbol && <p className="text-xs text-[color:var(--c-gray-400)] mt-1">Symbol: {r.symbol}</p>}
                      {r.payload && Object.keys(r.payload).length > 0 && (
                        <pre className="mt-2 text-xs font-mono text-[color:var(--c-gray-100)] overflow-x-auto whitespace-pre-wrap break-words">
                          {JSON.stringify(r.payload, null, 2)}
                        </pre>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default RiskIntelligenceSection;
